import {skipToken} from '@reduxjs/toolkit/query';
import {ButtonVariant} from 'platform/components';

import {defaultTo, isNil, sortBy} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  HeaderDynamicActionsResponse,
  useGetWarehouseHeaderDynamicActionsQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {createHeaderActions} from '@dms/shared';

import {Nullish} from 'shared/web';

type ActionPresentation = {
  order: number;
  variant: ButtonVariant;
};

/**
 * The backend sends every core exchange action without `optionClick`, `position` or `variant`, so the
 * action bar of the Figma design (process order, cancelling actions outlined in red) is defined here.
 * An action key the backend adds later is rendered after these, as a primary button.
 */
const ACTION_PRESENTATION: Record<string, ActionPresentation> = {
  CORE_WILL_NOT_BE_DELIVERED: {order: 1, variant: 'dangerOutlined'},
  DELIVERED_BY_CUSTOMER: {order: 2, variant: 'primary'},
  RECORD_INSPECTION: {order: 3, variant: 'primary'},
  RECEIVE_TO_WAREHOUSE: {order: 4, variant: 'primary'},
  SEND_RETURN_AUTHORISATION_REQUEST: {order: 5, variant: 'primary'},
  RETURN_DENIED: {order: 6, variant: 'dangerOutlined'},
  RETURN_AUTHORISED: {order: 7, variant: 'primary'},
};

const UNKNOWN_ACTION_ORDER = 100;

const getPresentation = (key: string): ActionPresentation =>
  Object.hasOwn(ACTION_PRESENTATION, key)
    ? ACTION_PRESENTATION[key]
    : {order: UNKNOWN_ACTION_ORDER, variant: 'primary'};

type ActionOption = HeaderDynamicActionsResponse['actions']['options'][number];

// Without CORE_EXCHANGE_EDIT every process step is locked, whatever style the backend sends.
const lockWithoutEditPermission = (option: ActionOption): ActionOption =>
  option.style === 'hidden'
    ? option
    : {...option, style: 'disabled', tooltip: {label: i18n.t('general.labels.noPermission')}};

// While the detail or its actions refetch after a process step, the bar still shows the previous state.
const lockWhileRefreshing = (option: ActionOption): ActionOption =>
  option.style === 'hidden' ? option : {...option, style: 'disabled'};

const toActionBar = (
  actions: HeaderDynamicActionsResponse['actions'] | Nullish,
  canEdit: boolean,
  isRefreshing: boolean
): HeaderDynamicActionsResponse['actions'] | null => {
  if (isNil(actions)) {
    return null;
  }

  const options = sortBy((option) => getPresentation(option.key).order, actions.options)
    .map((option) => ({
      ...option,
      variant: defaultTo(getPresentation(option.key).variant, option.variant),
    }))
    .map((option) => (canEdit ? option : lockWithoutEditPermission(option)))
    .map((option) => (isRefreshing ? lockWhileRefreshing(option) : option));

  return {optionClick: options.map((option) => option.key), options};
};

/**
 * Action bar of the core exchange detail, rendered from GET warehouse/v1/core-exchange/{coreExchangeId}/actions.
 * The backend decides per action whether it is enabled, disabled or hidden; the action dialogs
 * invalidate the `headerDynamicActions` tag on success, so the bar refetches after each process step.
 */
export function useCoreExchangeHeaderActions(
  coreExchangeId: string,
  onActionClick: (actionKey: string, actionTitle: string) => void,
  canEdit: boolean,
  isCoreExchangeFetching: boolean
) {
  const {
    data: coreExchangeActions,
    isLoading: areActionsLoading,
    isFetching: areActionsFetching,
    isError: hasActionsError,
  } = useGetWarehouseHeaderDynamicActionsQuery(
    isNilOrEmpty(coreExchangeId) ? skipToken : {id: coreExchangeId, resource: 'core-exchange'}
  );

  // The dialog title is the action title the backend sends, already translated.
  const handleActionClick = (actionKey: string) => {
    const action = coreExchangeActions?.actions.options.find((option) => option.key === actionKey);
    onActionClick(actionKey, defaultTo(actionKey, action?.title));
  };

  const headerActions = createHeaderActions({
    actions: toActionBar(
      coreExchangeActions?.actions,
      canEdit,
      isCoreExchangeFetching || areActionsFetching
    ),
    callback: handleActionClick,
  });

  return {headerActions, areActionsLoading, hasActionsError};
}
